import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboard, getPendingUsers, approveUser, rejectUser } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Clock, AlertTriangle, AlertOctagon, Calendar, PlusCircle, UserPlus, Check, X } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#22c55e', '#facc15', '#ef4444'];

const DashboardPage = ({ onNavigate }) => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  useEffect(() => {
    loadDashboard();
    loadPendingUsers();
  }, []);

  const loadDashboard = async () => {
    try {
      const result = await getDashboard();
      setData(result);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const result = await getPendingUsers();
      setPendingUsers(result);
    } catch (error) {
      console.error('Error loading pending users:', error);
    } finally {
      setLoadingPending(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await approveUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (error) {
      alert('Erro ao aprovar usuário');
    }
  };

  const handleRejectUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja recusar este cadastro? O usuário será removido.')) {
      try {
        await rejectUser(userId);
        setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      } catch (error) {
        alert('Erro ao recusar usuário');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const chartData = [
    { name: 'Disponível', value: data?.ok || 0 },
    { name: 'Acabando', value: data?.low_balance || 0 },
    { name: 'Excedido', value: data?.exceeded || 0 }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ESTOUROU':
        return <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-bold border border-red-200">Excedido</span>;
      case 'ACABANDO':
        return <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-bold border border-yellow-200">Baixo</span>;
      default:
        return <span className="px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-bold border border-green-200">Disponível</span>;
    }
  };

  const getBalanceColor = (status) => {
    switch (status) {
      case 'ESTOUROU':
        return 'text-red-600 font-bold';
      case 'ACABANDO':
        return 'text-yellow-600 font-bold';
      default:
        return 'text-green-600 font-bold';
    }
  };

  return (
    <div className="space-y-6 fade-in">
      <header className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Painel de Controle</h2>
          <p className="text-slate-500 text-sm">Visão geral da disponibilidade dos parceiros.</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => onNavigate('calendar')}
            className="border-slate-300"
            data-testid="nav-calendar-btn"
          >
            <Calendar className="h-4 w-4 mr-2" /> Ver Cronograma
          </Button>
          <Button 
            onClick={() => onNavigate('requests')}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="nav-schedule-btn"
          >
            <PlusCircle className="h-4 w-4 mr-2" /> Sinalizar Indisponibilidade
          </Button>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-slate-200" data-testid="kpi-total">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Parceiros</p>
              <h3 className="text-3xl font-heading font-bold text-slate-800">{data?.total_providers || 0}</h3>
            </div>
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-full">
              <Users className="h-6 w-6" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200" data-testid="kpi-pending">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Notificações Pendentes</p>
              <h3 className="text-3xl font-heading font-bold text-purple-600">{data?.pending_requests || 0}</h3>
            </div>
            <div className="p-3 bg-purple-50 text-purple-600 rounded-full">
              <Clock className="h-6 w-6" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200" data-testid="kpi-warning">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Saldo Baixo</p>
              <h3 className="text-3xl font-heading font-bold text-yellow-600">{data?.low_balance || 0}</h3>
            </div>
            <div className="p-3 bg-yellow-50 text-yellow-600 rounded-full">
              <AlertTriangle className="h-6 w-6" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200" data-testid="kpi-danger">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Excedeu Limite</p>
              <h3 className="text-3xl font-heading font-bold text-red-600">{data?.exceeded || 0}</h3>
            </div>
            <div className="p-3 bg-red-50 text-red-600 rounded-full">
              <AlertOctagon className="h-6 w-6" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Users Section */}
      {pendingUsers.length > 0 && (
        <Card className="border-orange-200 bg-orange-50" data-testid="pending-users-card">
          <CardHeader className="border-b border-orange-200 pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-orange-800">
                <UserPlus className="h-5 w-5" />
                Cadastros Pendentes de Aprovação
              </CardTitle>
              <span className="bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded-full font-bold">
                {pendingUsers.length}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {pendingUsers.map((user) => (
                <div 
                  key={user.id} 
                  className="flex items-center justify-between bg-white p-4 rounded-lg border border-orange-100"
                  data-testid={`pending-user-${user.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{user.name}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApproveUser(user.id)}
                      data-testid={`approve-user-${user.id}`}
                    >
                      <Check className="h-4 w-4 mr-1" /> Aprovar
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleRejectUser(user.id)}
                      data-testid={`reject-user-${user.id}`}
                    >
                      <X className="h-4 w-4 mr-1" /> Recusar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Table */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="providers-table">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="font-heading text-lg">Controle de Disponibilidade</CardTitle>
              <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-1 rounded flex items-center gap-1">
                Regra Híbrida (&gt;5 = Corridos)
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase text-xs tracking-wide">
                  <tr>
                    <th className="px-6 py-3">Parceiro</th>
                    <th className="px-6 py-3">Área</th>
                    <th className="px-6 py-3 text-center">Limite Anual</th>
                    <th className="px-6 py-3 text-center">Dias Consumidos</th>
                    <th className="px-6 py-3 text-center">Saldo</th>
                    <th className="px-6 py-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data?.providers_stats?.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50 transition-colors" data-testid={`provider-row-${p.id}`}>
                      <td className="px-6 py-4 font-medium text-slate-900">{p.name}</td>
                      <td className="px-6 py-4 text-slate-500 text-xs">
                        <span className="bg-slate-100 px-2 py-1 rounded text-slate-600">{p.area}</span>
                      </td>
                      <td className="px-6 py-4 text-center text-slate-500">{p.limit}</td>
                      <td className="px-6 py-4 text-center text-slate-500">{p.days_used}</td>
                      <td className={`px-6 py-4 text-center ${getBalanceColor(p.status)}`}>{p.balance}</td>
                      <td className="px-6 py-4 text-center">{getStatusBadge(p.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!data?.providers_stats || data.providers_stats.length === 0) && (
                <div className="p-8 text-center text-slate-400">
                  <p>Nenhum parceiro cadastrado.</p>
                  {isAdmin() && (
                    <button onClick={() => onNavigate('settings')} className="text-indigo-600 hover:underline mt-2 text-sm">
                      Cadastrar agora
                    </button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chart Section */}
        <Card className="border-slate-200" data-testid="availability-chart">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="font-heading text-sm text-slate-500 uppercase tracking-wide">Disponibilidade Anual</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <div className="flex justify-between border-b border-slate-100 pb-1">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div> Disponível (&gt; 5 dias)
                </span>
                <span className="font-bold">{data?.ok || 0}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-400"></div> Acabando (0-5 dias)
                </span>
                <span className="font-bold">{data?.low_balance || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div> Excedido (&lt; 0 dias)
                </span>
                <span className="font-bold">{data?.exceeded || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
